import React, { useCallback, useState, useEffect } from 'react';
import { 
  Typography, 
  Checkbox, 
  FormControlLabel, 
  Button, 
  useTheme, 
  Box,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField
} from '@mui/material';
import { DataGrid, GridColDef, GridRenderCellParams, GridToolbarFilterButton, GridToolbarExport, GridToolbarColumnsButton, GridToolbarDensitySelector, Toolbar } from '@mui/x-data-grid';
import { useTranslation } from 'react-i18next';
import { LicenseResponseDto, LicenseStatus, FileTypeVariantsDto, UpdateLicenseDto, FileTypeLicenseDto } from '../../apiClient/Api';
import { useApplicationContext } from 'ApplicationContext';
import { useSearchParams } from 'react-router';
import dayjs from 'dayjs';

const AdminRequestedLicenses: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { t } = useTranslation();
  const [requests, setRequests] = useState<LicenseResponseDto[]>([]);
  const [fileTypes, setFileTypes] = useState<FileTypeLicenseDto[]>([]);
  const [showOnlyOpen, setShowOnlyOpen] = useState(true);
  const [updateDialogOpen, setUpdateDialogOpen] = useState(false);
  const [revokeDialogOpen, setRevokeDialogOpen] = useState(false);
  const [selectedLicense, setSelectedLicense] = useState<LicenseResponseDto | null>(null);
  const [selectedProduct, setSelectedProduct] = useState<string>('');
  const [selectedVariant, setSelectedVariant] = useState<string | null>(null);
  const [variants, setVariants] = useState<FileTypeVariantsDto[]>([]);
  const [updateDescription, setUpdateDescription] = useState<string>('');
  const [revokeProduct, setRevokeProduct] = useState<string>('');
  const [revokeVariant, setRevokeVariant] = useState<string | null>(null);
  const [revokeVariants, setRevokeVariants] = useState<FileTypeVariantsDto[]>([]);
  const [supportCodeFilter, setSupportCodeFilter] = useState<string>('');
  const [activeSupportCodeFilter, setActiveSupportCodeFilter] = useState<string>('');
  const context = useApplicationContext();
  const theme = useTheme();

  const fetchVariants = useCallback(
    async (fileTypeId: string, setter: (variants: FileTypeVariantsDto[]) => void) => {
      try {
        const response = await context.api.fileType.fileTypeGetFileType(fileTypeId);
        setter(response.data.variants ?? []);
      } catch (e) {
        setter([]);
        context.apiError(e);
      }
    },
    [context.api.fileType, context.apiError],
  );

  // Compute filtered requests based on status and support code
  const filteredRequests = requests.filter((request) => {
    // Status filter
    const statusMatch = showOnlyOpen 
      ? request.status === LicenseStatus.Ordered
      : request.status !== LicenseStatus.Revoked;
    
    // Support code filter - use activeSupportCodeFilter instead of supportCodeFilter
    const supportCodeMatch = !activeSupportCodeFilter.trim() || 
      request.supportCodeId?.toUpperCase().includes(activeSupportCodeFilter.trim().toUpperCase());
    
    return statusMatch && supportCodeMatch;
  });

  useEffect(() => {
    const fetchRequests = async () => {
      const response = await context.api.service.serviceGetAllRequestedLicenses();
      setRequests(response.data);
      const licenseVersions = await context.api.service.serviceGetLicenseVersions();
      // Sort by name
      const sortedVersions = licenseVersions.data.sort((a, b) => 
        (a.name || '').localeCompare(b.name || '')
      );
      setFileTypes(sortedVersions);
    };

    fetchRequests();
  }, [context.api.service]);

  // Check for support code in URL parameters on mount
  useEffect(() => {
    const supportCodeParam = searchParams.get('supportCode');
    if (supportCodeParam) {
      setSupportCodeFilter(supportCodeParam);
      setActiveSupportCodeFilter(supportCodeParam);
    }
  }, [searchParams]);

  // Update variants when product changes
  useEffect(() => {
    if (selectedProduct) {
      fetchVariants(selectedProduct, setVariants);
      setSelectedVariant(null);
    } else {
      setVariants([]);
      setSelectedVariant(null);
    }
  }, [selectedProduct, fetchVariants]);

  // Update revoke variants when revoke product changes
  useEffect(() => {
    if (revokeProduct) {
      fetchVariants(revokeProduct, setRevokeVariants);
      setRevokeVariant(null);
    } else {
      setRevokeVariants([]);
      setRevokeVariant(null);
    }
  }, [revokeProduct, fetchVariants]);

  /**
   * Toggles between showing only open licenses (Ordered) or all except revoked.
   */
  const toggleOpenFilter = () => {
    setShowOnlyOpen(!showOnlyOpen);
  };

  /**
   * Handles the generation of a license for a specific request and updates the row with the returned data.
   * 
   * @param requestId - The ID of the request
   */
  const handleGenerateLicense = async (requestId: string) => {
    const response = await context.api.service.serviceGenerateAndStoreLicense(requestId);
    setRequests((prevRequests) =>
      prevRequests.map((request) =>
        request.id === requestId ? response.data : request
      )
    );
  };

  /**
   * Opens the update dialog for a license request.
   * 
   * @param license - The license request to update
   */
  const handleOpenUpdateDialog = (license: LicenseResponseDto) => {
    setSelectedLicense(license);
    setSelectedProduct(license.fileTypeId || '');
    setSelectedVariant(license.tag || null);
    setUpdateDescription(license.description || '');
    setUpdateDialogOpen(true);
  };

  /**
   * Closes the update dialog and resets the form.
   */
  const handleCloseUpdateDialog = () => {
    setUpdateDialogOpen(false);
    setSelectedLicense(null);
    setSelectedProduct('');
    setSelectedVariant(null);
    setUpdateDescription('');
  };

  /**
   * Handles updating a license request with new product/variant information.
   */
  const handleUpdateLicense = async () => {
    if (!selectedLicense || !selectedProduct) return;

    const updateDto: UpdateLicenseDto = {
      fileTypeId: selectedProduct,
      tag: selectedVariant,
      description: updateDescription || null
    };

    const response = await context.api.service.serviceUpdateLicense(selectedLicense.id!, updateDto);
    setRequests((prevRequests) =>
      prevRequests.map((request) =>
        request.id === selectedLicense.id ? response.data : request
      )
    );
    handleCloseUpdateDialog();
  };

  /**
   * Opens the revoke dialog for a generated license.
   * 
   * @param license - The license to revoke and remap
   */
  const handleOpenRevokeDialog = (license: LicenseResponseDto) => {
    setSelectedLicense(license);
    setRevokeProduct(license.fileTypeId || '');
    setRevokeVariant(license.tag || null);
    setRevokeDialogOpen(true);
  };

  /**
   * Closes the revoke dialog and resets the form.
   */
  const handleCloseRevokeDialog = () => {
    setRevokeDialogOpen(false);
    setSelectedLicense(null);
    setRevokeProduct('');
    setRevokeVariant(null);
  };

  /**
   * Handles revoking a license without remapping.
   */
  const handleRevokeOnly = async () => {
    if (!selectedLicense) return;

    const response = await context.api.service.serviceRevokeLicense(selectedLicense.id!);
    setRequests((prevRequests) =>
      prevRequests.map((request) =>
        request.id === selectedLicense.id ? response.data : request
      )
    );
    handleCloseRevokeDialog();
  };

  /**
   * Handles revoking and remapping a generated license to a new product/variant.
   */
  const handleRevokeAndRemap = async () => {
    if (!selectedLicense || !revokeProduct) return;

    const updateDto: UpdateLicenseDto = {
      fileTypeId: revokeProduct,
      tag: revokeVariant,
      description: null
    };

    const response = await context.api.service.serviceRevokeAndRemapLicense(selectedLicense.id!, updateDto);
    setRequests((prevRequests) =>
      prevRequests.map((request) =>
        request.id === selectedLicense.id ? response.data : request
      )
    );
    handleCloseRevokeDialog();
  };

  /**
   * Gets the translated status text based on the request status.
   * 
   * @param status - The status of the license request
   * @returns The translated status text
   */
  const getStatusText = (status: LicenseStatus): string => {
    switch (status) {
      case LicenseStatus.Ordered:
        return t('adminRequestedLicenses.statusPending');
      case LicenseStatus.LicenseGenerated:
        return t('adminRequestedLicenses.statusApproved');
      case LicenseStatus.Rejected:
        return t('adminRequestedLicenses.statusRejected');
      case LicenseStatus.Revoked:
        return t('adminRequestedLicenses.statusRevoked');
      default:
        return t('adminRequestedLicenses.statusUnknown');
    }
  };

  /**
   * Gets the row color based on the request status.
   * 
   * @param status - The status of the license request
   * @returns The row color
   */
  const getRowColor = (status: LicenseStatus) => {
    switch (status) {
      case LicenseStatus.Rejected:
        return theme.palette.error.main;
      case LicenseStatus.Ordered:
        return theme.palette.warning.main;
      case LicenseStatus.LicenseGenerated:
        return theme.palette.success.main;
      case LicenseStatus.Revoked:
        return theme.palette.error.main;
      default:
        return theme.palette.warning.main;
    }
  };

  const getTextColor = (status: LicenseStatus): string => {
    switch (status) {
      case LicenseStatus.Rejected:
      case LicenseStatus.Revoked:
        return theme.palette.error.contrastText;
      case LicenseStatus.Ordered:
        return theme.palette.warning.contrastText;
      case LicenseStatus.LicenseGenerated:
        return theme.palette.success.contrastText;
      default:
        return theme.palette.warning.contrastText;
    }
  };

  /**
   * Applies the support code filter and updates URL.
   */
  const applySupportCodeFilter = () => {
    setActiveSupportCodeFilter(supportCodeFilter);
    if (supportCodeFilter.trim()) {
      // Update URL parameter
      setSearchParams({ supportCode: supportCodeFilter.trim() });
    } else {
      // Remove URL parameter
      setSearchParams({});
    }
  };

  /**
   * Clears the support code filter and removes URL parameter.
   */
  const clearSupportCodeFilter = () => {
    setSupportCodeFilter('');
    setActiveSupportCodeFilter('');
    setSearchParams({});
  };

  const CustomToolbar = () => {
    return (
      <Toolbar style={{ justifyContent: 'flex-start', gap: 8, flexWrap: 'wrap' }}>
        <GridToolbarColumnsButton />
        <GridToolbarFilterButton />
        <GridToolbarDensitySelector />
        <GridToolbarExport />
        <Button 
          size="small"
          onClick={toggleOpenFilter}
          sx={{ ml: 1 }}
        >
          {showOnlyOpen ? t('adminRequestedLicenses.showAll') : t('adminRequestedLicenses.showOnlyOpen')}
        </Button>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 2 }}>
          <TextField
            size="small"
            placeholder={t('adminRequestedLicenses.filterBySupportCode')}
            value={supportCodeFilter}
            onChange={(e) => setSupportCodeFilter(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                applySupportCodeFilter();
              }
            }}
            sx={{ width: 200 }}
          />
          <Button 
            size="small"
            variant="contained"
            onClick={applySupportCodeFilter}
          >
            {t('adminRequestedLicenses.filter')}
          </Button>
          {activeSupportCodeFilter && (
            <Button 
              size="small"
              variant="outlined"
              onClick={clearSupportCodeFilter}
            >
              {t('adminRequestedLicenses.clearFilter')}
            </Button>
          )}
        </Box>
      </Toolbar>
    );
  };

  const columns: GridColDef[] = [
    {
      field: 'status',
      headerName: t('adminRequestedLicenses.status'),
      width: 130,
      type: 'singleSelect',
      valueOptions: [
        { value: LicenseStatus.Ordered, label: t('adminRequestedLicenses.statusPending') },
        { value: LicenseStatus.LicenseGenerated, label: t('adminRequestedLicenses.statusApproved') },
        { value: LicenseStatus.Rejected, label: t('adminRequestedLicenses.statusRejected') },
        { value: LicenseStatus.Revoked, label: t('adminRequestedLicenses.statusRevoked') }
      ],
      renderCell: (params: GridRenderCellParams) => (
        <Chip 
          label={getStatusText(params.value as LicenseStatus)} 
          size="small"
          sx={{ 
            backgroundColor: getRowColor(params.value as LicenseStatus),
            color: getTextColor(params.value as LicenseStatus),
            fontWeight: 'bold'
          }}
        />
      )
    },
    {
      field: 'requestedAt',
      headerName: t('adminRequestedLicenses.requestedAt'),
      width: 110,
      type: 'date',
      valueGetter: (value) => new Date(value),
      valueFormatter: (value) => dayjs(value).format('DD.MM.YYYY')
    },
    {
      field: 'userName',
      headerName: t('adminRequestedLicenses.userName'),
      width: 150,
      flex: 0.5
    },
    {
      field: 'hostId',
      headerName: t('adminRequestedLicenses.requestId'),
      width: 180,
      renderCell: (params: GridRenderCellParams) => (
        <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.85rem', lineHeight: 'inherit' }}>
          {params.value}
        </Typography>
      )
    },
    {
      field: 'supportCodeId',
      headerName: t('adminRequestedLicenses.supportCode'),
      width: 140
    },
    {
      field: 'supportContract',
      headerName: t('adminRequestedLicenses.supportContract'),
      width: 140
    },
    {
      field: 'description',
      headerName: t('adminRequestedLicenses.description'),
      width: 250,
      flex: 1
    },
    {
      field: 'fileTypeId',
      headerName: t('adminRequestedLicenses.fileType'),
      width: 150,
      type: 'singleSelect',
      valueOptions: fileTypes.map(ft => ({ value: ft.id, label: ft.name })),
      renderCell: (params: GridRenderCellParams) => {
        const fileType = fileTypes.find(ft => ft.id === params.value);
        return fileType?.name || params.value;
      }
    },
    {
      field: 'tag',
      headerName: t('adminRequestedLicenses.tags'),
      width: 120,
      renderCell: (params: GridRenderCellParams) => 
        params.value ? (
          <Chip label={params.value} size="small" />
        ) : (
          <Typography variant="body2" sx={{ opacity: 0.5, fontSize: '0.85rem' }}>
            {t('adminRequestedLicenses.noTags')}
          </Typography>
        )
    },
    {
      field: 'licenseGeneratedAt',
      headerName: t('adminRequestedLicenses.licenseGeneratedAt'),
      width: 120,
      valueFormatter: (value) => value ? dayjs(value).format('DD.MM.YYYY') : '-'
    },
    {
      field: 'licenseCode',
      headerName: t('adminRequestedLicenses.licenseCode'),
      width: 200,
      renderCell: (params: GridRenderCellParams) => 
        params.value ? (
          <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.85rem', lineHeight: 'inherit' }}>
            {params.value}
          </Typography>
        ) : null
    },
    {
      field: 'actions',
      headerName: '',
      width: 300,
      sortable: false,
      renderCell: (params: GridRenderCellParams) => {
        const status = params.row.status as LicenseStatus;
        
        if (status === LicenseStatus.Ordered) {
          return (
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button 
                variant="contained" 
                size="small"
                onClick={() => handleGenerateLicense(params.row.id)}
                sx={{ 
                  textTransform: 'none',
                  fontSize: '0.8rem'
                }}
              >
                {t('adminRequestedLicenses.generateLicense')}
              </Button>
              <Button 
                variant="outlined" 
                size="small"
                onClick={() => handleOpenUpdateDialog(params.row)}
                sx={{ 
                  textTransform: 'none',
                  fontSize: '0.8rem'
                }}
              >
                {t('adminRequestedLicenses.updateLicense')}
              </Button>
            </Box>
          );
        } else if (status === LicenseStatus.LicenseGenerated) {
          return (
            <Button 
              variant="outlined" 
              color="error"
              size="small"
              onClick={() => handleOpenRevokeDialog(params.row)}
              sx={{ 
                textTransform: 'none',
                fontSize: '0.8rem'
              }}
            >
              {t('adminRequestedLicenses.revokeLicense')}
            </Button>
          );
        }
        return null;
      }
    }
  ];

  return (
    <Box sx={{ width: '100%', py: 2 }}>
      <Typography variant="h4" sx={{ mb: 2 }}>
        {t('adminRequestedLicenses.title')}
      </Typography>
      
      <Box sx={{ height: 'calc(100vh - 200px)', width: '100%' }}>
        <DataGrid
          rows={filteredRequests}
          columns={columns}
          initialState={{
            pagination: {
              paginationModel: { pageSize: 25, page: 0 }
            },
            sorting: {
              sortModel: [{ field: 'requestedAt', sort: 'desc' }]
            }
          }}
          pageSizeOptions={[10, 25, 50, 100]}
          disableRowSelectionOnClick
          getRowId={(row) => row.id!}
          showToolbar
          slots={{
            toolbar: CustomToolbar
          }}
          sx={{
            '& .MuiDataGrid-row': {
              cursor: 'pointer'
            }
          }}
        />
      </Box>

      {/* Update License Dialog */}
      <Dialog open={updateDialogOpen} onClose={handleCloseUpdateDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{t('adminRequestedLicenses.updateDialogTitle')}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <FormControl fullWidth>
              <InputLabel>{t('adminRequestedLicenses.selectProduct')}</InputLabel>
              <Select
                value={selectedProduct}
                onChange={(e) => setSelectedProduct(e.target.value)}
                label={t('adminRequestedLicenses.selectProduct')}
              >
                {fileTypes.map((fileType) => (
                  <MenuItem key={fileType.id} value={fileType.id}>
                    {fileType.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {variants.length > 0 && (
              <FormControl fullWidth>
                <InputLabel>{t('adminRequestedLicenses.selectVariant')}</InputLabel>
                <Select
                  value={selectedVariant || ''}
                  onChange={(e) => setSelectedVariant(e.target.value || null)}
                  label={t('adminRequestedLicenses.selectVariant')}
                >
                  <MenuItem value="">{t('licenseGenerator.default')}</MenuItem>
                  {variants.map((variant) => (
                    <MenuItem key={variant.tag} value={variant.tag}>
                      {variant.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            <TextField
              fullWidth
              multiline
              rows={3}
              label={t('adminRequestedLicenses.updateDescription')}
              value={updateDescription}
              onChange={(e) => setUpdateDescription(e.target.value)}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseUpdateDialog}>
            {t('adminRequestedLicenses.cancel')}
          </Button>
          <Button 
            onClick={handleUpdateLicense} 
            variant="contained"
            disabled={!selectedProduct}
          >
            {t('adminRequestedLicenses.update')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Revoke and Remap License Dialog */}
      <Dialog open={revokeDialogOpen} onClose={handleCloseRevokeDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{t('adminRequestedLicenses.revokeDialogTitle')}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {t('adminRequestedLicenses.revokeDialogDescription')}
          </Typography>
          <Typography variant="subtitle2" sx={{ mb: 2 }}>
            {t('adminRequestedLicenses.revokeOnly')}: {t('adminRequestedLicenses.revokeConfirm')}
          </Typography>
          <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 'bold' }}>
            {t('adminRequestedLicenses.revokeAndRemap')}:
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <FormControl fullWidth>
              <InputLabel>{t('adminRequestedLicenses.selectProduct')}</InputLabel>
              <Select
                value={revokeProduct}
                onChange={(e) => setRevokeProduct(e.target.value)}
                label={t('adminRequestedLicenses.selectProduct')}
              >
                {fileTypes.map((fileType) => (
                  <MenuItem key={fileType.id} value={fileType.id}>
                    {fileType.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {revokeVariants.length > 0 && (
              <FormControl fullWidth>
                <InputLabel>{t('adminRequestedLicenses.selectVariant')}</InputLabel>
                <Select
                  value={revokeVariant || ''}
                  onChange={(e) => setRevokeVariant(e.target.value || null)}
                  label={t('adminRequestedLicenses.selectVariant')}
                >
                  <MenuItem value="">{t('licenseGenerator.default')}</MenuItem>
                  {revokeVariants.map((variant) => (
                    <MenuItem key={variant.tag} value={variant.tag}>
                      {variant.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseRevokeDialog}>
            {t('adminRequestedLicenses.cancel')}
          </Button>
          <Button 
            onClick={handleRevokeOnly} 
            variant="outlined"
            color="error"
          >
            {t('adminRequestedLicenses.revokeOnly')}
          </Button>
          <Button 
            onClick={handleRevokeAndRemap} 
            variant="contained"
            color="error"
            disabled={!revokeProduct}
          >
            {t('adminRequestedLicenses.revokeAndRemap')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminRequestedLicenses;
